/**
 * Issue Highlight Component
 *
 * Renders a highlighted text span for an issue with severity-based
 * styling and footnote superscript badge.
 *
 * Uses <Text> for true inline flow within parent Text element.
 * Click triggers popover/bottom sheet.
 *
 * NOTE: On React Native Web, refs on nested <Text> elements resolve to
 * React internal instances rather than DOM nodes. We use event-based
 * measurement (e.currentTarget.getBoundingClientRect()) for reliable
 * popover positioning.
 */

import React, { useCallback, useRef } from 'react';
import {
  Text,
  Platform,
  View,
  GestureResponderEvent,
  NativeSyntheticEvent,
  TargetedEvent,
} from 'react-native';
import type { IssueHighlightProps, AnchorPosition } from './types';
import { styles, getSeverityStyles } from './inlineAnnotationsStyles';

/**
 * IssueHighlight - Inline highlighted text span
 *
 * IMPORTANT: This component returns a <Text> element that must be
 * rendered inside a parent <Text> for proper inline text flow.
 */
export function IssueHighlight({
  issue,
  issueNumber,
  isActive,
  onHover,
  onPress,
  onHoverEnd,
  testID,
}: IssueHighlightProps) {
  const highlightRef = useRef<Text>(null);
  const severityStyles = getSeverityStyles(issue.severity);

  /**
   * Get position from event target (Web) or ref measurement (Native)
   * On Web, event.currentTarget reliably returns the DOM element
   * even when refs fail on nested Text components.
   */
  const getPositionFromEvent = useCallback(
    (
      event: GestureResponderEvent | React.MouseEvent<HTMLElement>,
      callback: (position: AnchorPosition) => void
    ) => {
      if (Platform.OS === 'web') {
        // Web: Use event.currentTarget for reliable DOM access
        const target = (event as React.MouseEvent<HTMLElement>).currentTarget;
        if (target && typeof target.getBoundingClientRect === 'function') {
          const rect = target.getBoundingClientRect();
          callback({
            x: rect.left,
            y: rect.top,
            width: rect.width,
            height: rect.height,
          });
        }
      } else {
        // Native: Use ref-based measurement
        if (highlightRef.current) {
          (highlightRef.current as unknown as View).measure?.(
            (x, y, width, height, pageX, pageY) => {
              callback({
                x: pageX,
                y: pageY,
                width,
                height,
              });
            }
          );
        }
      }
    },
    []
  );

  // Handle press/click - use event for measurement
  const handlePress = useCallback(
    (event: GestureResponderEvent) => {
      getPositionFromEvent(event, (position) => {
        onPress?.(issue.id, position);
      });
    },
    [issue.id, onPress, getPositionFromEvent]
  );

  // Handle mouse enter (web) - use event for measurement
  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (Platform.OS === 'web' && onHover) {
        getPositionFromEvent(event, (position) => {
          onHover(issue.id, position);
        });
      }
    },
    [issue.id, onHover, getPositionFromEvent]
  );

  // Handle mouse leave (web)
  const handleMouseLeave = useCallback(() => {
    if (Platform.OS === 'web' && onHoverEnd) {
      onHoverEnd();
    }
  }, [onHoverEnd]);

  // Determine styling based on status
  const isAccepted = issue.status === 'accepted';
  const isIgnored = issue.status === 'ignored';

  // Web-specific hover props
  const webHoverProps =
    Platform.OS === 'web'
      ? {
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
        }
      : {};

  return (
    <Text
      ref={highlightRef}
      onPress={handlePress}
      testID={testID || `issue-highlight-${issue.id}`}
      accessibilityRole="button"
      accessibilityLabel={`Issue ${issueNumber}: ${issue.title}`}
      accessibilityHint="Tap to see details and suggestions"
      style={[
        styles.issueHighlightInline,
        severityStyles.highlight,
        isActive && styles.issueHighlightActive,
        isIgnored && styles.ignoredHighlight,
        Platform.OS === 'web' && { cursor: 'pointer' as unknown as undefined },
      ]}
      {...webHoverProps}
    >
      <Text
        style={[
          styles.issueTextInline,
          severityStyles.text,
          isAccepted && styles.acceptedText,
        ]}
      >
        {issue.snippet}
      </Text>
      <Text style={[styles.footnoteSuperscript, severityStyles.badge] as any}>
        {issueNumber}
      </Text>
    </Text>
  );
}

export default IssueHighlight;
